/**
 * How the Audit log page reads a row (app#348): the action menu, the readable action label, the
 * actor, the target cell, and the fallback when the router does not know the `q` search.
 *
 * ⚠ PURE, and imports nothing that needs Vite: `npm test` runs `auditLog.test.ts` with
 * `node --experimental-strip-types`.
 *
 * ⚠⚠ THE STATIC ACTION LIST IS ONLY THE FALLBACK. A router with router#1561 returns the org's own
 * distinct actions (`org_audit_list` with `include_actions: true`), and the menu is built from that
 * with `groupAuditActions`. The static list is for a router that refuses `include_actions`.
 * It mirrors the router. The source is `actionFor` in router
 * `src/tools/audit_action.go`, the `credit` refinements in `src/tools/credit.go`
 * (`creditAuditActions`), the two cancellation rows in `src/billing/events.go`, the grant rows in
 * `src/router/admin_grants.go`, `node.stop` from the automatic stop (router#1551), and
 * `org.directory_read`, `service.check`, `service.share`, `node.grant`, `node.revoke` and
 * `node.label` from `privilegedActionFor` (router#1906). A new
 * router action still renders: `auditActionLabel` makes a label from the raw name, and the search
 * box finds it. It is only missing from the menu.
 */

import { withRosterName, type Person } from './userLabel.ts'

/** One row of `org_audit_list`, camelcased. The fields after `createdAt` come with router#1551. */
export type AuditRow = {
  action: string
  actorId?: string
  actorEmail?: string
  targetType?: string
  targetId?: string
  targetName?: string
  targetNetworkId?: string
  targetDeleted?: boolean
  reason?: string
  metadata?: Record<string, string>
  /** True on a noBGP staff (support) action (router#1772, router v0.4.179). Absent otherwise. */
  privileged?: boolean
  /** On a staff row: what admitted it. `org_setting` or `support_grant`. Also stamped on a refused
   * row, so read it with `ok`. See `auditSupportMarker`. */
  supportAccess?: string
}

export type AuditActionGroup = { group: string; actions: string[] }

/**
 * The readable label of each action the router can record. The keys are ALSO the static menu.
 *
 * ⚠ The menu uses them ONLY when the router refuses `include_actions` (before router#1561). A
 * router that has it sends the org's own actions, and those build the menu; this table then only
 * gives their labels.
 */
const ACTION_LABELS: Record<string, string> = {
  // Node lifecycle.
  'node.provision': 'Node provisioned',
  'node.deprovision': 'Node deprovisioned',
  'node.register': 'Node registered',
  'node.rename': 'Node renamed',
  'node.transfer': 'Node given to another member',
  'node.stop': 'Node stopped',
  'node.deadline_set': 'Node time limit set',
  // router#1906 writes the next three only for noBGP support, and staff are always refused them,
  // so each row has ok=false.
  'node.grant': 'Local MCP turned on',
  'node.revoke': 'Local MCP turned off',
  'node.label': 'Node labels changed',
  // Node access.
  'node.command': 'Command run on node',
  'node.file': 'File operation on node',
  'node.fs_read': 'File read on node',
  'node.fs_write': 'File written on node',
  'node.fs_list': 'Directory listed on node',
  'node.fs_stat': 'File checked on node',
  'node.fs_delete': 'File deleted on node',
  'node.fs_mkdir': 'Directory created on node',
  'node.fs_edit': 'File edited on node',
  'node.fs_copy': 'File copied between nodes',
  'node.fs_grep': 'Files searched on node',
  'node.fs_glob': 'Files matched on node',
  'node.logs': 'Node logs read',
  'node.config_get': 'Node configuration read',
  'node.config_set': 'Node configuration changed',
  'node.net_peers': 'Node peers read',
  'node.net_interfaces': 'Node interfaces read',
  'node.net_metrics': 'Node metrics read',
  'node.net_routes': 'Node routes read',
  'node.net_dns': 'Node DNS read',
  'node.net_reach': 'Reachability checked from node',
  'node.net_reach_many': 'Reachability checked from nodes',
  'node.fs_subscribe': 'File watch started',
  'node.command_subscribe': 'Command subscription started',
  'node.presence_subscribe': 'Presence watch started',
  'node.fs_grep_subscribe': 'File search subscription started',
  // Network.
  'network.create': 'Network created',
  'network.delete': 'Network deleted',
  'network.update': 'Network settings changed',
  // Service.
  'service.publish': 'Service published',
  'service.update': 'Service updated',
  'service.delete': 'Service deleted',
  // router#1906: rows only for noBGP support. `service.share` is always refused (ok=false). It is
  // "managed", not "changed": `service_share` also has a `list` operation.
  'service.check': 'Service checked',
  'service.share': 'Service visitors managed',
  // router#1980: every `service_share` call is a row, one action for each operation. The bare
  // `service.share` above stays for rows written before that.
  'service.share_list': 'Service visitors read',
  'service.share_add': 'Service visitor added',
  'service.share_remove': 'Service visitor removed',
  'service.share_revoke': 'Service visitors cleared',
  'service.share_approve': 'Access request accepted',
  'service.share_reject': 'Access request rejected',
  // Member.
  'member.invite': 'Member invited',
  'member.invite_resend': 'Invitation sent again',
  'member.invite_revoke': 'Invitation revoked',
  'member.accept': 'Invitation accepted',
  'member.remove': 'Member removed',
  'member.role_change': 'Member role changed',
  'member.leave': 'Member left',
  // Organization.
  'org.create': 'Organization created',
  'org.ownership_transfer': 'Ownership transferred',
  'org.sso_setup': 'SSO set up',
  'org.sso_set_enforced': 'SSO enforcement changed',
  'org.support_read': 'noBGP support setting viewed',
  'org.support_setting': 'noBGP support setting changed',
  'org.support_grant': 'Temporary noBGP support access given',
  'org.support_end': 'Temporary noBGP support access ended',
  // A noBGP support person read the org's network directory by org_id (router#1906). Always a
  // staff row (`privileged`).
  'org.directory_read': 'Network directory read by noBGP support',
  'feedback.submit': 'Feedback sent',
  // Billing.
  'billing.read': 'Billing viewed',
  'billing.checkout': 'Plan checkout started',
  'billing.portal': 'Billing portal opened',
  'billing.spend_cap': 'Spending limit changed',
  'billing.spend_cap_cleared': 'Spending limit cleared',
  'billing.credit': 'Credit request',
  'billing.credit_status': 'Credit viewed',
  'billing.credit_purchases': 'Credit purchases viewed',
  'billing.credit_buy': 'Credit bought',
  'billing.credit_auto_reload': 'Auto-refill changed',
  'billing.credit_auto_reload_cleared': 'Auto-refill cleared',
  'billing.credit_spending': 'Credit spending changed',
  'billing.usage_grant': 'Usage grant added',
  'billing.usage_grant_expire': 'Usage grant ended',
}

/** The group each action prefix goes in, in menu order. `feedback.*` goes with the organization. */
const GROUPS: [string, string[]][] = [
  ['Node', ['node.']],
  ['Network', ['network.']],
  ['Service', ['service.']],
  ['Member', ['member.']],
  ['Organization', ['org.', 'feedback.']],
  ['Billing', ['billing.']],
]

/** The group of an action the prefixes above do not know. It goes last. */
const OTHER_GROUP = 'Other'

/**
 * Groups a list of actions for the menu. An empty group is left out, and an action with an unknown
 * prefix goes in "Other". Inside a group the order of `actions` is kept: the router sends its list
 * sorted, and the static list is in the order of `ACTION_LABELS`.
 */
export const groupAuditActions = (actions: readonly string[]): AuditActionGroup[] => {
  const unique = [...new Set(actions.filter(Boolean))]
  const groups: AuditActionGroup[] = GROUPS.map(([group, prefixes]) => ({
    group,
    actions: unique.filter(action => prefixes.some(p => action.startsWith(p))),
  }))
  const other = unique.filter(action => !GROUPS.some(([, prefixes]) => prefixes.some(p => action.startsWith(p))))
  groups.push({ group: OTHER_GROUP, actions: other })
  return groups.filter(g => g.actions.length > 0)
}

/**
 * The FALLBACK action menu: every action this module knows, grouped. Used only when the router
 * refuses `include_actions` (a router before router#1561).
 */
export const AUDIT_ACTION_GROUPS: AuditActionGroup[] = groupAuditActions(Object.keys(ACTION_LABELS))

/**
 * The words for a stop reason. The router copies the task's stored stop code (`db.TaskStopReason`)
 * to the row's `reason` (router#1561):
 *
 * - `node.stop` with no actor (the compute-stop sweeper): `deadline`, `allowance`, `credit_empty`,
 *   `lapsed`, or `provider`.
 * - `node.stop` from `task_stop`: `user_stop` or `provider`.
 * - `node.deprovision`: `user` or `provider`.
 *
 * `provider` means the container had already stopped when the call or the sweeper came to it.
 * `included_compute` and `spend_cap` are the names router#1551 first proposed; they stay so an
 * early row still reads as words.
 */
const STOP_REASONS: Record<string, string> = {
  deadline: 'time limit reached',
  allowance: 'included compute used up',
  included_compute: 'included compute used up',
  credit_empty: 'credit balance empty',
  spend_cap: 'spending limit reached',
  lapsed: 'subscription ended',
  provider: 'the machine had already stopped',
  user: 'a person removed the machine',
  user_stop: 'a person stopped the machine',
}

/** The words for a stop reason. An unknown reason shows with its underscores as spaces. */
export const stopReasonLabel = (reason: string): string =>
  STOP_REASONS[reason] ?? reason.replace(/_/g, ' ')

/** `node.fs_read` → "Node fs read": the label of an action this module does not know. */
const fallbackLabel = (action: string): string => {
  const words = action.replace(/[._]/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : action
}

/** The reason a row carries: the entry field (router#1551), else `metadata.reason`. */
export const auditReason = (row: Pick<AuditRow, 'reason' | 'metadata'>): string | undefined =>
  row.reason || row.metadata?.reason || undefined

/** The readable action. A row with a reason adds it: "Node stopped: time limit reached". */
export const auditActionLabel = (row: Pick<AuditRow, 'action' | 'reason' | 'metadata'>): string => {
  const label = ACTION_LABELS[row.action] ?? fallbackLabel(row.action)
  const reason = auditReason(row)
  return reason ? `${label}: ${stopReasonLabel(reason)}` : label
}

/**
 * The actor as a person (app#401), for `UserLabel`: named from the org's member list when the page
 * has it (an Owner or Admin reads the audit log, and the roster). Null when the router did it by
 * itself: the page then shows `auditActorLabel`.
 */
export const auditActor = (
  row: Pick<AuditRow, 'actorEmail' | 'actorId'>,
  roster: Parameters<typeof withRosterName>[1]
): Person | null =>
  row.actorEmail || row.actorId ? withRosterName({ id: row.actorId, email: row.actorEmail }, roster) : null

/** The actor cell. No actor means the router did it by itself (a webhook, the stop sweeper). */
export const auditActorLabel = (row: Pick<AuditRow, 'actorEmail' | 'actorId'>): string =>
  row.actorEmail || row.actorId || 'noBGP (automatic)'

/**
 * The words for `support_access`: what admitted a staff action. The values are
 * `db.SupportAccessOrgSetting` and `db.SupportAccessGrant` in router `src/db/audit.go`
 * (migration 0225, router#1772).
 */
const SUPPORT_ACCESS: Record<string, string> = {
  org_setting: 'The organization setting "Allow noBGP support" allowed this action.',
  support_grant: 'Temporary noBGP support access from an Owner or Admin allowed this action.',
}

export type AuditSupportMarker = {
  /** The chip text. */
  label: string
  /** The chip tooltip: what allowed the action. */
  tooltip: string
}

/**
 * The marker of a noBGP support action (app#421), or null on every other row.
 *
 * ⚠ `privileged` is the key, NOT `supportAccess`. A staff row can have `privileged` and no
 * `support_access`: a row written before router#1772, an attempt the org refused (support off, no
 * grant), or a grant that ended during the call. It is still a staff action. It gets the chip with
 * a tooltip that does not say what allowed it. An unknown `support_access` value also gets the
 * chip, and the tooltip shows the raw value.
 *
 * ⚠⚠ A row with `ok: false` is an ATTEMPT. The router stamps `support_access` from the org's
 * setting on a refused row too (router `stampStaffSupport`), so the tooltip must not say that
 * something "allowed" an action that did not complete.
 */
export const auditSupportMarker = (
  row: Pick<AuditRow, 'privileged' | 'supportAccess'> & { ok?: boolean },
): AuditSupportMarker | null => {
  if (!row.privileged) return null
  if (row.ok === false)
    return { label: 'noBGP support', tooltip: 'A noBGP support person tried this action. It did not complete.' }
  const access = row.supportAccess ? SUPPORT_ACCESS[row.supportAccess] ?? `Allowed by: ${row.supportAccess.replace(/_/g, ' ')}.` : null
  return {
    label: 'noBGP support',
    tooltip: access ? `A noBGP support person did this action. ${access}` : 'A noBGP support person did this action.',
  }
}

export type AuditTargetCell = {
  /** The text to show. */
  text: string
  /** The app page of the target, or null when there is no page to go to. */
  href: string | null
  /** The raw `type: id`, for the tooltip. Null when the row has no target. */
  tooltip: string | null
}

/**
 * The target cell.
 *
 * - With a name: the name, and "(deleted)" after it when the target no longer exists.
 * - Without a name (a router before router#1551): the old `type: id` text.
 * - A link only for a target that still exists and has a page: a node or a network, by its id.
 */
export const auditTargetCell = (row: AuditRow): AuditTargetCell => {
  if (!row.targetType) return { text: '—', href: null, tooltip: null }
  const raw = row.targetId ? `${row.targetType}: ${row.targetId}` : row.targetType
  if (!row.targetName) return { text: row.targetDeleted ? `${raw} (deleted)` : raw, href: null, tooltip: null }

  const text = row.targetDeleted ? `${row.targetName} (deleted)` : row.targetName
  let href: string | null = null
  if (!row.targetDeleted && row.targetId) {
    if (row.targetType === 'node') {
      href = `/nodes/${encodeURIComponent(row.targetId)}`
    } else if (row.targetType === 'network') {
      href = `/networks/${encodeURIComponent(row.targetId)}`
    }
  }
  return { text, href, tooltip: raw }
}

/**
 * Whether a refusal says the router does not know the argument `field`.
 *
 * ⚠⚠ AN OLDER ROUTER REFUSES AN UNKNOWN ARGUMENT, IT DOES NOT IGNORE IT. `/api/v1/tools/{name}`
 * decodes with `DisallowUnknownFields` (`strictUnmarshal`, router `src/tools/registry.go`), so the
 * call comes back `invalid_args` with `invalid arguments: json: unknown field "<field>"`. That is
 * `q` before router#1553 and `include_actions` before router#1561. The check asks for the code AND
 * the field name, so an `invalid_args` about another field (a bad `org_id`) is not taken for this.
 */
export const isUnknownFieldRefusal = (
  code: string | undefined,
  message: string | undefined,
  field: string
): boolean => code === 'invalid_args' && (message ?? '').includes(`unknown field "${field}"`)

/**
 * The page-side search, used only when the router refused `q`: a case-insensitive substring of
 * the action, its label, the actor's email or name, the target id or the target name. It searches
 * the rows the page has loaded, not the whole log, and the page says so.
 *
 * ⚠ The actor's NAME comes from `roster`, the same member list the actor cell shows (router#1888):
 * the name a reader sees must find the row, as the router's `q` does from router PR #1889.
 * `roster` has no default, so a call site that forgets it does not compile.
 */
export const matchesAuditSearch = (
  row: AuditRow,
  q: string,
  roster: Parameters<typeof withRosterName>[1]
): boolean => {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  const actorName = auditActor(row, roster)?.name
  return [row.action, auditActionLabel(row), row.actorEmail, actorName, row.targetId, row.targetName].some(
    field => !!field && field.toLowerCase().includes(needle)
  )
}

/** The node page's History link: the audit log with the node id as the search text. */
export const nodeHistoryHref = (nodeId: string): string => `/account/audit?q=${encodeURIComponent(nodeId)}`
